import { z } from 'zod';
import { statutSchema } from './commun';

/** Longueur minimale d'une saisie avant d'interroger l'API. */
export const LONGUEUR_MIN_RECHERCHE = 2;
export const LONGUEUR_MAX_RECHERCHE = 100;

export const MODES_RECHERCHE = ['auto', 'adresse', 'base', 'lieu'] as const;

export const rechercheQuerySchema = z.object({
  q: z.string().trim().max(LONGUEUR_MAX_RECHERCHE).default(''),
  /**
   * `adresse` force la recherche d'adresses (action "Chercher comme adresse"). `base` ne cherche que dans la
   * base, sans attendre la Géoplateforme : le front l'utilise pour afficher les résultats locaux tout de suite.
   * `lieu` ne cherche que les communes et localités (France, Suisse, Belgique, Luxembourg), pour le choix
   * d'une ville dans « Autour de moi ».
   */
  mode: z.enum(MODES_RECHERCHE).default('auto'),
});
export type RechercheQuery = z.infer<typeof rechercheQuerySchema>;

const MOTS_DE_VOIE =
  /\b(rue|avenue|av|boulevard|bd|bld|chemin|route|rte|place|impasse|allee|all[ée]e|quai|cours|passage|zone|za|zi|zac|lieu[- ]?dit|resid|r[ée]sidence)\b/i;

/**
 * Vrai si la saisie ressemble à une adresse : un numéro suivi d'un mot, ou un mot de voie
 * (rue, avenue, chemin...). Les saisies de type "85000" ou "Vendée" ne sont pas des adresses.
 */
export function ressembleAUneAdresse(q: string): boolean {
  const texte = q.trim();
  if (texte.length < 5) return false;
  if (MOTS_DE_VOIE.test(texte)) return true;
  return /^\d{1,4}\s*(bis|ter|quater)?[\s,]+\p{L}{2,}/iu.test(texte);
}

const clientTrouveSchema = z.object({
  id: z.number().int(),
  raisonSociale: z.string(),
  ville: z.string().nullable(),
  codePostal: z.string().nullable(),
  statut: statutSchema.nullable(),
  localise: z.boolean(),
});

const personneTrouveeSchema = z.object({
  /** Clé technique, utilisée comme valeur du filtre mentor ou consultant. */
  cle: z.string(),
  nom: z.string(),
  nbClientsMentor: z.number().int(),
  nbClientsConsultant: z.number().int(),
});

/** Code ISO du pays d'une commune ou d'une adresse trouvée. */
const paysTrouveSchema = z.enum(['FR', 'CH', 'BE', 'LU']);

const communeTrouveeSchema = z.object({
  /** Code INSEE pour une commune française, `CH-123` (pays et numéro de localité) ailleurs. */
  id: z.string(),
  nom: z.string(),
  codePostal: z.string().nullable(),
  /** Département (France), canton (Suisse), province (Belgique) ou district (Luxembourg). */
  departement: z.string(),
  pays: paysTrouveSchema,
  lon: z.number(),
  lat: z.number(),
});

const adresseTrouveeSchema = z.object({
  libelle: z.string(),
  type: z.string().nullable(),
  score: z.number(),
  pays: paysTrouveSchema,
  lon: z.number(),
  lat: z.number(),
});

export const rechercheReponseSchema = z.object({
  clients: z.array(clientTrouveSchema),
  personnes: z.array(personneTrouveeSchema),
  communes: z.array(communeTrouveeSchema),
  adresses: z.array(adresseTrouveeSchema),
  /** Vrai si les adresses devaient être cherchées mais qu'aucun service n'a répondu. */
  adressesIndisponibles: z.boolean(),
  /** Vrai si la saisie ressemble à une adresse (le front peut l'afficher en premier). */
  ressembleAdresse: z.boolean(),
});
export type RechercheReponse = z.infer<typeof rechercheReponseSchema>;
export type ClientTrouve = z.infer<typeof clientTrouveSchema>;
export type PersonneTrouvee = z.infer<typeof personneTrouveeSchema>;
export type CommuneTrouvee = z.infer<typeof communeTrouveeSchema>;
export type AdresseTrouvee = z.infer<typeof adresseTrouveeSchema>;

/** Nom de l'événement window émis pour centrer la carte sur un lieu (commune ou adresse). */
export const EVENEMENT_CENTRER_CARTE = 'carte:centrer';

export interface DestinationCarte {
  lon: number;
  lat: number;
  zoom: number;
  libelle: string;
}
